import {Injectable} from "@angular/core";
import {ViewportScroller} from "@angular/common";
import {NavigationEnd, NavigationStart, Router} from "@angular/router";

/**
 * App-wide scroll restoration: back and forward restore the position saved for that history entry, a
 * real path change scrolls to top, and anything else — a query-param cleanup, a resolver
 * re-activation, a URL self-correction — leaves the position alone.
 *
 * The router's own `scrollPositionRestoration: 'top'` is too blunt for this, since it fires on every
 * NavigationEnd and so jumps to top late on navigations the user never made. It must be set to
 * 'disabled' for this service to be the single source of truth. Initialize once from AppComponent.
 */
@Injectable({providedIn: 'root'})
export class ScrollRestorationService {

    private readonly positions = new Map<number, [number, number]>();
    private currentNavId: number = null;
    private lastPath: string = null;
    private popstateRestoreId: number = null;
    private initialized: boolean = false;

    constructor(
        private router: Router,
        private viewportScroller: ViewportScroller
    ) {
    }

    init(): void {
        if (this.initialized) {
            return;
        }
        this.initialized = true;

        this.router.events.subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.onNavigationStart(event);
            } else if (event instanceof NavigationEnd) {
                this.onNavigationEnd(event);
            }
        });
    }

    private onNavigationStart(event: NavigationStart): void {
        // snapshot where we are before leaving, keyed by the navigation that rendered this page
        if (this.currentNavId != null) {
            this.positions.set(this.currentNavId, this.viewportScroller.getScrollPosition());
        }
        this.popstateRestoreId = event.navigationTrigger === 'popstate' && event.restoredState
            ? event.restoredState.navigationId
            : null;
    }

    private onNavigationEnd(event: NavigationEnd): void {
        const path = this.stripQueryAndFragment(event.urlAfterRedirects);
        const savedPosition = this.popstateRestoreId != null ? this.positions.get(this.popstateRestoreId) : undefined;

        if (savedPosition) {
            // defer a tick so the destination view has rendered enough height to scroll into
            setTimeout(() => this.viewportScroller.scrollToPosition(savedPosition));
        } else if (path !== this.lastPath) {
            this.viewportScroller.scrollToPosition([0, 0]);
        }

        this.currentNavId = event.id;
        this.lastPath = path;
        this.popstateRestoreId = null;
    }

    private stripQueryAndFragment(url: string): string {
        return url.split('#')[0].split('?')[0];
    }
}
